import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Download, 
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Send,
  FileText,
  AlertCircle,
  Image as ImageIcon,
  Video,
  File
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { useToast } from '../../hooks/useToast'
import { getVersion, getFileUrl } from '../../services/fileService'
import { 
  createComment, 
  getVersionComments,
  approveVersion,
  requestChanges,
  getProjectAuditLogs
} from '../../services/approvalService'
import { generateApprovalProof } from '../../services/pdfService'
import { getProject } from '../../services/projectService'
import Toast from '../../components/Toast'

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return ImageIcon
  if (fileType?.startsWith('video/')) return Video
  return File
}

const actionIcons = {
  version_uploaded: FileText,
  version_approved: CheckCircle,
  changes_requested: XCircle,
  comment_added: MessageSquare
}

export default function VersionDetail() {
  const { projectId, versionId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { success, error: showError, warning, toastState, closeToast } = useToast()
  const [version, setVersion] = useState(null)
  const [project, setProject] = useState(null)
  const [comments, setComments] = useState([])
  const [auditLogs, setAuditLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [filePreviewUrl, setFilePreviewUrl] = useState(null)
  const [fileLoadError, setFileLoadError] = useState(false)

  useEffect(() => {
    loadVersion()
  }, [versionId])

  const loadVersion = async () => {
    try {
      setLoading(true)
      const versionData = await getVersion(versionId)
      setVersion(versionData)
      
      const projectData = await getProject(projectId)
      setProject(projectData)

      const { data: commentsData } = await getVersionComments(versionId)
      setComments(commentsData || [])

      const logsData = await getProjectAuditLogs(projectId)
      setAuditLogs(logsData || [])

      // Load file preview - don't fail entire page if file is missing
      if (versionData.file_path) {
        try {
          const url = await getFileUrl(versionData.file_path, 3600)
          setFilePreviewUrl(url)
          setFileLoadError(false)
        } catch (fileError) {
          console.error('Error loading file:', fileError)
          setFileLoadError(true)
          warning('File preview unavailable - the file may have been deleted or moved')
        }
      }
    } catch (error) {
      console.error('Error loading version:', error)
      showError('Failed to load version details')
      // Navigate back if version doesn't exist
      setTimeout(() => navigate(`/projects/${projectId}`), 2000)
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async (e) => {
    e.preventDefault()
    
    if (!commentText.trim()) {
      showError('Please enter a comment')
      return
    }

    try {
      setSubmitting(true)
      
      await createComment({
        version_id: versionId,
        user_id: user.id,
        content: commentText,
        metadata: {
          ip_address: 'internal',
          user_agent: navigator.userAgent
        }
      })

      setCommentText('')
      await loadVersion()
      success('Comment added successfully!')
    } catch (error) {
      console.error('Error adding comment:', error)
      showError('Failed to add comment')
    } finally {
      setSubmitting(false)
    }
  }

  const handleApprove = async () => {
    if (!window.confirm('Approve this version?')) return

    try {
      setSubmitting(true)
      
      await approveVersion(
        versionId,
        user.id,
        commentText || 'Approved',
        {
          ip_address: 'internal',
          user_agent: navigator.userAgent
        }
      )

      setCommentText('')
      await loadVersion()
      success('Version approved successfully!')
    } catch (error) {
      console.error('Error approving:', error)
      showError('Failed to approve: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRequestChanges = async () => {
    if (!commentText.trim()) {
      showError('Please describe the changes needed')
      return
    }

    if (!confirm('Request changes to this version?')) return

    try {
      setSubmitting(true)
      
      await requestChanges(
        versionId,
        user.id,
        commentText,
        {
          ip_address: 'internal',
          user_agent: navigator.userAgent
        }
      )

      setCommentText('')
      await loadVersion()
      success('Change request submitted!')
    } catch (error) {
      console.error('Error requesting changes:', error)
      showError('Failed to request changes: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleGeneratePDF = async () => {
    try {
      const approval = version.approvals?.find(a => a.status === 'approved')
      if (approval) {
        await generateApprovalProof(project, version, approval)
      } else {
        showError('No approval found for this version')
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      showError('Failed to generate PDF')
    }
  }

  const handleDownload = async () => {
    try {
      window.open(filePreviewUrl, '_blank')
    } catch (error) {
      console.error('Error downloading file:', error)
      showError('Failed to download file')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black dark:border-white"></div>
      </div>
    )
  }

  if (!version) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Version Not Found</h3>
        </div>
      </div>
    )
  }

  const FileIcon = getFileIcon(version.file_type)

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Link 
          to={`/projects/${projectId}`}
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 sm:mb-6 text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Project
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* File Preview */}
            <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <FileIcon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                  <div className="min-w-0">
                    <h2 className="font-display text-xl sm:text-2xl font-bold">Version {version.version_number}</h2>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{version.file_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Download</span>
                  </button>
                  {version.approvals?.some(a => a.status === 'approved') && (
                    <button
                      onClick={handleGeneratePDF}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-xl hover:bg-green-200 dark:hover:bg-green-900/30 text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="hidden sm:inline">Get PDF Proof</span>
                      <span className="sm:hidden">PDF</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden" style={{ minHeight: '500px' }}>
                {fileLoadError ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-gray-500 dark:text-gray-400">
                    <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <p className="text-lg font-medium mb-2">File No Longer Available</p>
                    <p className="text-sm">The file may have been deleted or moved</p>
                  </div>
                ) : (
                  <>
                    {version.file_type?.startsWith('image/') && filePreviewUrl && (
                      <img 
                        src={filePreviewUrl} 
                        alt={version.file_name}
                        className="w-full h-full object-contain"
                      />
                    )}
                    {version.file_type?.startsWith('video/') && filePreviewUrl && (
                      <video 
                        src={filePreviewUrl} 
                        controls
                        className="w-full h-full"
                      />
                    )}
                    {version.file_type === 'application/pdf' && filePreviewUrl && (
                      <iframe 
                        src={filePreviewUrl} 
                        className="w-full"
                        style={{ minHeight: '700px' }}
                      />
                    )}
                  </>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{new Date(version.created_at).toLocaleString()}</span>
                  <span className="sm:hidden">{new Date(version.created_at).toLocaleDateString()}</span>
                </span>
                <span>
                  {(version.file_size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            </div>

            {/* Comments */}
            <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
              <h3 className="font-display text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                Comments ({comments.length})
              </h3>

              <div className="space-y-2 sm:space-y-3 mb-4 max-h-96 overflow-y-auto">
                {comments.map(comment => (
                  <div key={comment.id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-0 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <span className="font-medium text-xs sm:text-sm truncate">
                          {comment.client_email || comment.user?.email || 'Unknown'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 break-words">{comment.content}</p>
                  </div>
                ))}

                {comments.length === 0 && (
                  <p className="text-center text-sm sm:text-base text-gray-500 py-6 sm:py-8">No comments yet</p>
                )}
              </div>

              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="space-y-2 sm:space-y-3">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  rows="3"
                  className="w-full px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 outline-none focus:ring-2 focus:ring-black dark:focus:ring-white text-sm sm:text-base"
                />
                <div className="flex gap-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={submitting || !commentText.trim()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
                  >
                    <Send className="w-4 h-4" />
                    Add Comment
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Actions */}
            <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
              <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Actions</h3>
              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={handleApprove}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Approve Version
                </button>
                <button
                  onClick={handleRequestChanges}
                  disabled={submitting || !commentText.trim()}
                  className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 text-sm sm:text-base"
                >
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Request Changes
                </button>
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
              <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Activity</h3>
              <div className="space-y-3 sm:space-y-4">
                {auditLogs.filter(log => log.version_id === versionId).slice(0, 10).map(log => {
                  const Icon = actionIcons[log.action_type] || Clock
                  return (
                    <div key={log.id} className="flex gap-3">
                      <div className="shrink-0">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {log.action_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )
                })}

                {auditLogs.filter(log => log.version_id === versionId).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No activity yet</p>
                )}
              </div>
            </div>

            {/* Approvals */}
            {version.approvals && version.approvals.length > 0 && (
              <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6">
                <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Approvals</h3>
                <div className="space-y-2 sm:space-y-3">
                  {version.approvals.map(approval => (
                    <div 
                      key={approval.id} 
                      className={`p-3 rounded-xl ${
                        approval.status === 'approved' 
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                          : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {approval.status === 'approved' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        )}
                        <span className="text-sm font-medium">
                          {approval.status === 'approved' ? 'Approved' : 'Changes Requested'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        By: {approval.user?.email || 'Client'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(approval.approved_at).toLocaleString()}
                      </p>
                      {approval.comment && (
                        <p className="text-sm mt-2">{approval.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
